import { useState, useEffect } from 'react';
import type { Theme } from '@/types';

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if user has a preference stored
    const stored = localStorage.getItem('theme') as Theme;
    if (stored) return stored;
    
    // Check system preference
    if (globalThis.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  useEffect(() => {
    const root = globalThis.document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Update favicon based on theme
    try {
      // Look for various favicon selectors (more comprehensive)
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement ||
                   document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement;
      
      const faviconHref = theme === 'dark' ? '/favicon-dark.png' : '/favicon-light.png';
      
      if (favicon) {
        favicon.href = faviconHref;
      } else {
        // Create favicon element if it doesn't exist
        console.warn('Favicon element not found, creating one');
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.type = 'image/png';
        newFavicon.href = faviconHref;
        document.head.appendChild(newFavicon);
      }
    } catch (error) {
      console.error('Failed to update favicon:', error);
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Listen for system theme changes when no user preference is stored
  useEffect(() => {
    const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a manual preference
      const storedTheme = localStorage.getItem('theme');
      if (!storedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return { theme, toggleTheme };
}
